import { FC, useCallback, useMemo, useState } from 'react'
import classNames from 'classnames'
import { InView } from 'react-intersection-observer'
import { Button } from '@/atoms/Button'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { slugs } from '@/constants/slugs'
import { formatPrice } from '@/services/PriceFormatter'
import { ShopifyProductWithBundles } from '@/services/Shopify/ShopifyService'
import { useShopifyCart } from '@/services/Shopify/hooks'
import { MoneyCurrencyCode } from '@/types/codegen-federation'
import { mergeUrlParams } from '@/utils/UriBuilder/UriBuilder'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { GiveawaySupportModal } from '@/views/PreSales/PreSalesGiveawayView/components/Modals/GiveawaySupportModal'
import { useGiveawayProductController } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawayProductController'
import { useGiveawaySchedule } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawaySchedule'
import { useShopifyUserAttributes } from '@/views/PreSales/hooks/useShopifyUserAttributes'
import useHandleUnexpectedError from '@/views/TicketCheckoutViews/hooks/useHandleUnexpectedError'
import {
  GButtonMD,
  GCaptionSM,
  GEyebrowMD,
  GEyebrowSM,
  GFooterMD,
  GTitleMD,
  GTitleSM,
  GTitleXXL,
} from '../UI/Typography'

interface Props {
  tickets: ShopifyProductWithBundles[]
  theatricalSlug: string
  entriesProductId: string
}

const PriceDisplay: FC<{
  selectedProduct: ShopifyProductWithBundles | undefined
  isLowerPricedTickets: boolean
  isNewDesign: boolean
  theatricalSlug: string
}> = ({ selectedProduct, isLowerPricedTickets, isNewDesign, theatricalSlug }) => {
  const { t } = useTranslate('tickets')
  return (
    <>
      {isNewDesign ? (
        <GEyebrowSM
          color="white"
          className={classNames(
            'mb-1 rounded-md  px-2 py-1 uppercase',
            isLowerPricedTickets ? 'visible' : 'invisible',
            'bg-giveaway-primary-accent-light',
            theatricalSlug === slugs.iWasAStranger && '!bg-giveaway-primary-accent-dark',
          )}
        >
          {t('tenPerTicket', '$10 per ticket')}
        </GEyebrowSM>
      ) : (
        <GEyebrowSM
          color="white"
          className={classNames(
            'mb-1 rounded-md  px-2 py-1 uppercase',
            isLowerPricedTickets ? 'visible' : 'invisible',
            theatricalSlug === slugs.senior ? 'bg-giveaway-primary-accent' : 'bg-giveaway-secondary-accent-dark',
          )}
        >
          {t('tenPerTicket', '$10 per ticket')}
        </GEyebrowSM>
      )}
      <FlexRow className="!items-top">
        <GTitleMD color="giveaway-text-primary-dark" className="mb-auto block !font-whitney !font-bold">
          $
        </GTitleMD>
        <GTitleXXL color="giveaway-text-primary-dark" className="items-top flex">
          {formatPrice((selectedProduct?.priceRange?.maxVariantPrice?.amount ?? 0) * 100, {
            currency: selectedProduct?.priceRange?.maxVariantPrice?.currencyCode ?? 'USD',
            ignoreEmptyDecimal: true,
            style: 'decimal',
          })}
        </GTitleXXL>
      </FlexRow>
    </>
  )
}

const EntriesDisplay: FC<{
  isActive: boolean
  multiplier: number
  totalSelectedEntries: number
  dailyBonusEntries: number
  selectedTicketsQty: number
  minRewardTicketQuantity: number | undefined
  theatricalSlug: string
  isNewDesign: boolean
}> = ({
  isActive,
  multiplier,
  totalSelectedEntries,
  dailyBonusEntries,
  selectedTicketsQty,
  minRewardTicketQuantity,
  theatricalSlug,
  isNewDesign,
}) => {
  const { t } = useTranslate('tickets')

  if (!isActive || multiplier <= 0) return null

  return (
    <FlexColumn className="mt-2">
      <div className="flex flex-col items-center">
        <GEyebrowSM
          className={classNames(
            'rounded-md  px-2 pb-[5px] pt-1',
            theatricalSlug === slugs.senior || slugs.david
              ? 'bg-giveaway-primary-accent-light'
              : 'bg-giveaway-secondary-accent-dark',
            theatricalSlug === slugs.iWasAStranger && '!bg-giveaway-primary-accent-dark',
          )}
          color="giveaway-text-primary-light"
        >
          {totalSelectedEntries} {t('giveawayEntriesV2', 'GIVEAWAY ENTRIES')}
        </GEyebrowSM>

        {dailyBonusEntries > 0 && selectedTicketsQty >= (minRewardTicketQuantity ?? 0) && (
          <GCaptionSM color="giveaway-text-primary-dark" className="mt-2" italic>
            <Translate i18nKey="dailyBonusEntries" values={{ dailyBonusEntries }} t={t}>
              Includes{' '}
              <span
                className={classNames(
                  'font-bold ',
                  isNewDesign ? 'text-giveaway-primary-accent' : 'text-giveaway-secondary-accent',
                )}
              >
                {{ dailyBonusEntries }} bonus entries
              </span>{' '}
              today only!
            </Translate>
          </GCaptionSM>
        )}

        {!dailyBonusEntries && (
          <GCaptionSM color="giveaway-text-primary-dark" className="mt-2" italic>
            <Translate i18nKey="bonusEntriesCardGiveawayV3" values={{ multiplier }} t={t}>
              Includes{' '}
              <span
                className={classNames(
                  'font-bold ',
                  isNewDesign ? 'text-giveaway-primary-accent' : 'text-giveaway-secondary-accent',
                )}
              >
                {{ multiplier }}x bonus entries
              </span>{' '}
              this week only!
            </Translate>
          </GCaptionSM>
        )}
      </div>
    </FlexColumn>
  )
}

const TicketQuantityButton: FC<{
  ticket: { id: string; quantity: number }
  isSelected: boolean
  theatricalSlug: string
  onSelect: () => void
}> = ({ ticket, isSelected, theatricalSlug, onSelect }) => {
  const buttonClasses = classNames(
    '!text-sm !border-none h-[40px] grow-1 !w-full relative',
    theatricalSlug === slugs.senior ? 'rounded-xl' : 'rounded-3xl',
    isSelected && !slugs.iWasAStranger ? '!bg-giveaway-primary-accent !text-white' : 'bg-[#D3D3D6B2]',
    isSelected && slugs.iWasAStranger && '!bg-giveaway-primary-dark !text-white',
  )

  return (
    <li className="!w-full min-w-[64px] max-w-[64px] md:!max-w-[70px]" key={ticket.id}>
      <Button onClick={onSelect} className={buttonClasses} variant="lightgray">
        {ticket.quantity}
      </Button>
    </li>
  )
}

export const PresaleChipSelectorCard: FC<Props> = ({ tickets, theatricalSlug, entriesProductId }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false)
  const { t } = useTranslate('tickets')
  const { trackProductAdded, trackProductViewed } = useEcommerceEvents()
  const { push } = useSafeRouter()
  const { handleUnexpectedError } = useHandleUnexpectedError({ reservationId: 'unknown' })
  const { createCart } = useShopifyCart()
  const { normal, isActive, minRewardTicketQuantity, dailyBonusEntries } = useGiveawaySchedule(theatricalSlug)
  const multiplier = normal?.multiplier ?? 1
  const isNewDesign = theatricalSlug === slugs.david || theatricalSlug === slugs.iWasAStranger
  const { selectedTicketsQty, selectedProduct, ticketQuantityList, totalSelectedEntries, handleProductChange } =
    useGiveawayProductController(tickets, theatricalSlug)

  const attributes = useShopifyUserAttributes('theatrical-giveaway', 'theatrical giveaway ticket', theatricalSlug)

  const handleCheckout = useCallback(
    async (product: ShopifyProductWithBundles) => {
      const merchandiseId = product?.variants?.edges[0]?.node?.id

      if (!merchandiseId) {
        handleUnexpectedError({
          context: 'AddPreOrderTicketOption',
          error: new Error('Merchandise ID is not available for the selected product.'),
          customToastMessage: t('somethingWentWrongPleaseTryAgainPreOrder', 'Something went wrong, please try again.'),
        })
        return
      }

      const lines: Array<{ quantity: number; merchandiseId: string; sellingPlanId?: string }> = [
        { quantity: 1, merchandiseId: merchandiseId },
        {
          quantity: totalSelectedEntries,
          merchandiseId: `gid://shopify/ProductVariant/${entriesProductId}`,
        },
      ]

      try {
        setIsLoading(true)

        const { cart, errors } = await createCart({
          lines,
          attributes,
        })

        if (errors && errors.length > 0) {
          setIsSupportModalOpen(true)
          return
        }

        const checkoutUrl = mergeUrlParams(cart?.checkoutUrl, { utm_term: 'from-angel-com-flow' })

        if (checkoutUrl) {
          await trackProductAdded({
            category: 'theatrical preorder ticket',
            funnel: 'theatrical-pre-sale',
            sku: `preorder-${theatricalSlug}`,
            product_id: `preorder-${theatricalSlug}`,
            name: `${theatricalSlug} theatrical preorder ticket`,
            price: parseInt(product?.priceRange?.maxVariantPrice?.amount ?? 0),
            currency: (product?.priceRange?.maxVariantPrice?.currencyCode as MoneyCurrencyCode) ?? 'USD',
            quantity: 1,
            projectSlug: theatricalSlug,
          })
          return push(checkoutUrl)
        }
      } catch (error) {
        handleUnexpectedError({
          context: 'AddPreOrderTicketOption',
          error,
          customToastMessage: t('somethingWentWrongPleaseTryAgainPreOrder', 'Something went wrong, please try again.'),
        })
      } finally {
        setIsLoading(false)
      }
    },
    [
      attributes,
      createCart,
      entriesProductId,
      handleUnexpectedError,
      push,
      t,
      theatricalSlug,
      totalSelectedEntries,
      trackProductAdded,
    ],
  )

  const isLowerPricedTickets = useMemo(() => {
    return selectedTicketsQty >= 30
  }, [selectedTicketsQty])

  function handleInitialCheckoutIntent(): void {
    selectedProduct && handleCheckout(selectedProduct)
  }

  return (
    <InView
      triggerOnce
      onChange={(inView) => {
        if (!inView) return

        trackProductViewed({
          category: 'theatrical pre-order ticket',
          funnel: 'theatrical-pre-sale',
          sku: `pre-order-${theatricalSlug}`,
          product_id: `pre-order-${theatricalSlug}`,
          name: `${theatricalSlug} theatrical pre-order ticket`,
          price: Number((selectedProduct?.priceRange?.maxVariantPrice?.amount ?? 0) * 100),
          currency: 'USD',
          quantity: 1,
          projectSlug: theatricalSlug,
        })
      }}
    >
      <div className="flex flex-col">
        <div
          className={classNames(
            'mx-auto flex w-full flex-col gap-6  border-2 bg-[#F5F5F5] px-8 py-6  md:bg-white',
            theatricalSlug === slugs.sketch ? 'rounded-none' : 'rounded-3xl',
            isNewDesign ? 'border-giveaway-primary-subtle ' : 'border-giveaway-secondary-accent',
          )}
        >
          <div className="flex flex-col items-center gap-1">
            <PriceDisplay
              selectedProduct={selectedProduct ?? undefined}
              isLowerPricedTickets={isLowerPricedTickets}
              isNewDesign={isNewDesign}
              theatricalSlug={theatricalSlug}
            />
            <FlexColumn className="text-center">
              <GTitleSM color="giveaway-text-subtle-light">
                {t('fullPriceTicketsSelectedQty', '{{selectedQty}} Tickets', { selectedQty: selectedTicketsQty })}
              </GTitleSM>
            </FlexColumn>
            <EntriesDisplay
              isActive={isActive}
              multiplier={multiplier}
              totalSelectedEntries={totalSelectedEntries}
              dailyBonusEntries={dailyBonusEntries}
              selectedTicketsQty={selectedTicketsQty}
              minRewardTicketQuantity={minRewardTicketQuantity}
              theatricalSlug={theatricalSlug}
              isNewDesign={isNewDesign}
            />
          </div>

          <div className="flex flex-col gap-4">
            <GEyebrowMD color="giveaway-text-subtle-light" className="uppercase">
              {t('ticketQuantityPreSales', 'Ticket Quantity')}:
            </GEyebrowMD>

            <ul className="flex grow-1 flex-wrap justify-center gap-3">
              {ticketQuantityList.map((ticket) => (
                <TicketQuantityButton
                  key={ticket.id}
                  ticket={ticket}
                  isSelected={ticket?.id === selectedProduct?.id}
                  theatricalSlug={theatricalSlug}
                  onSelect={() => handleProductChange(ticket.id)}
                />
              ))}
            </ul>
          </div>

          {selectedProduct && (
            <button
              disabled={isLoading}
              onClick={() => handleInitialCheckoutIntent()}
              className={classNames(
                ' px-5 py-[0.906rem] bg-giveaway-secondary',
                slugs.david && '!bg-giveaway-primary-subtle',
                slugs.iWasAStranger && '!bg-giveaway-secondary-accent',
                theatricalSlug === slugs.sketch ? 'rounded-none' : 'rounded-xl',
              )}
            >
              <GButtonMD color="white" className="flex w-full items-center justify-between">
                <span className="flex items-center gap-2">
                  {t('next', 'Next')}
                  {isLoading && <LoadingSpinnerIcon size={18} color="white" />}
                </span>
                <span className="gap flex items-center text-white/80">
                  {formatPrice((selectedProduct?.priceRange?.maxVariantPrice?.amount ?? 0) * 100, {
                    currency: selectedProduct?.priceRange?.maxVariantPrice?.currencyCode ?? 'USD',
                    ignoreEmptyDecimal: true,
                  })}
                  <CaretRightIcon color="white" className="opacity-80" size={28} />
                </span>
              </GButtonMD>
            </button>
          )}
        </div>
        <GFooterMD color="giveaway-text-subtle-light" className="mx-auto mt-6 max-w-[420px] px-4">
          {theatricalSlug === slugs.david ? (
            <Translate t={t} i18nKey="presaleChipDisclaimerDavid2">
              DAVID releases on December 19, 2025. Promo codes will be emailed after purchase. Redeem them at{' '}
              <a href={`https://angel.com/tickets`} target="_blank" className="underline">
                angel.com/tickets
              </a>{' '}
              when showtimes are available. Questions? Check our FAQs!
            </Translate>
          ) : (
            <Translate t={t} i18nKey="presaleChipDisclaimerV4" values={{ theatricalSlug }}>
              Your ticket codes will be emailed after purchase. Redeem them at{' '}
              <a href={`https://angel.com/tickets/${theatricalSlug}`} target="_blank" className="underline">
                angel.com/tickets/{{ theatricalSlug }}
              </a>{' '}
              when showtimes are available. Questions? Check our FAQs!
            </Translate>
          )}
        </GFooterMD>
      </div>

      <GiveawaySupportModal
        open={isSupportModalOpen}
        onClose={() => {
          setIsSupportModalOpen(false)
        }}
      />
    </InView>
  )
}
